<script>import genericSpinner from "../assets/spinner.svg";
import { Button } from "@infinitro/button";
import { t, Verbiage } from "@infinitro/content";
import { ModalDialog, modalStack, popModal } from "@infinitro/global";
import { WarningNewIcon } from "@infinitro/icons";
import { InfoCard } from "@infinitro/info-card";
import { Modal } from "@infinitro/modal";
import { Text } from "@infinitro/text";
import { onMount } from "svelte";
import { derived } from "svelte/store";
import { sign, stateConnectedWalletSigned } from "../state/web3";
const thisModal = ModalDialog.WalletVerify;
const modalIndex = derived(
  modalStack,
  ($modalStack) => $modalStack.findIndex((e) => e.modal === thisModal)
);
const isModalOpen = derived(modalIndex, ($modalIndex) => $modalIndex >= 0);
const payload = derived(
  modalStack,
  ($modalStack) => $modalStack.find((e) => e.modal === thisModal)?.data
);
const zOverlay = derived(
  modalIndex,
  ($modalIndex) => $modalIndex >= 0 ? 9999 + $modalIndex * 2 : 9999
);
const closeModal = () => popModal(thisModal);
let errors = /* @__PURE__ */ new Set();
function addError(message) {
  errors.clear();
  errors.add(message);
  errors = new Set(errors);
}
function clearErrors() {
  errors.clear();
  errors = new Set(errors);
}
let signing = false;
const handleSign = async () => {
  if (!$payload?.address || !$payload?.statement || !$payload?.nonce) {
    addError("Address, statement and nonce are required");
    return;
  }
  clearErrors();
  signing = true;
  try {
    const [success, signedMessage, errorMessage] = await sign(
      $payload.address,
      $payload.statement,
      $payload.nonce
    );
    if (success) {
      const [pass, errorMessage2] = await $payload.onSigned(signedMessage);
      console.log("pass", pass);
      console.log("signedMessage", signedMessage);
      if (pass === false) {
        addError(errorMessage2 ?? "unknown error");
        return;
      }
    } else {
      addError(errorMessage ?? "unknown error");
      return;
    }
    closeModal();
  } finally {
    signing = false;
  }
};
const handleCancel = async () => {
  try {
    if ($payload?.onCancel) {
      const [pass, errorMessage] = await $payload.onCancel();
      if (pass === false) {
        addError(errorMessage ?? "unknown error");
        return;
      }
    }
    closeModal();
  } catch (e) {
    console.error("error handling left button click", e);
    addError(e);
  }
};
const handleClose = async () => {
  try {
    if ($payload?.onClose) {
      const pass = await $payload.onClose();
      if (pass === false) return;
    }
    closeModal();
  } catch (e) {
    console.error("error handling close", e);
    addError(e);
  }
};
function fitText(node) {
  const minSize = 6;
  function adjust() {
    node.style.fontSize = "";
    let size = parseFloat(getComputedStyle(node).fontSize);
    while (node.scrollWidth > node.clientWidth && size > minSize) {
      size -= 0.5;
      node.style.fontSize = `${size}px`;
    }
  }
  adjust();
  const ro = new ResizeObserver(adjust);
  ro.observe(node);
  const mo = new MutationObserver(adjust);
  mo.observe(node, { childList: true, characterData: true, subtree: true });
  return {
    destroy() {
      ro.disconnect();
      mo.disconnect();
    }
  };
}
onMount(() => {
  stateConnectedWalletSigned.set(false);
});
</script>

{#if $isModalOpen}
	<Modal
		title={$payload?.title}
		closeModal={handleClose}
		width="450px"
		maxWidth="90vw"
		noAnimation={$modalIndex > 0 || ($payload?.noAnimation ?? false)}
		zIndexOverlay={$zOverlay}
		zIndexUnderlay={$zOverlay - 1}
	>
		{#if errors.size > 0}
			<div class="error-box">
				<WarningNewIcon />
				<small class="withdraw-disclaimer">
					<strong>{`${$t(Verbiage.error)}:`}</strong>
					<span>{Array.from(errors).join(' ')}</span>
				</small>
			</div>
		{/if}
		<div class="login-via-wallet-content">
			{#if !signing}
				<InfoCard>
					<div class="address-group">
						<Text>Ethereum Address</Text>
						<Text bodySize="small" textColor="success">{$t(Verbiage.connected)}</Text>

						<Text><code use:fitText>{$payload?.address}</code></Text>
					</div>
				</InfoCard>
				{#if $payload?.content}
					<InfoCard>
						<div class="address-group">
							<Text>{@html $payload?.content}</Text>
						</div>
					</InfoCard>
				{/if}
			{:else}
				<InfoCard>
					<div class="address-group">
						<Text bodySize="large" textAlign="center">{$t(Verbiage.signature_required)}</Text>
						<center><img src={genericSpinner} alt="..." class="spinner" /></center>
						<Text>{$payload?.statement}</Text>
						<Text bodySize="small"
							>{$t(Verbiage.please_check_wallet_and_accept_the_request_to_continue)}</Text
						>
					</div>
				</InfoCard>
			{/if}
			<div class="actions">
				<Button
					variant="tertiary"
					text={$t(Verbiage.cancel)}
					onClick={handleCancel}
					size="medium"
					fullWidth
				/>
				<Button
					variant="primary"
					text={$t(Verbiage.sign) + ($stateConnectedWalletSigned ? ' ✅' : '')}
					onClick={handleSign}
					fullWidth
					size="medium"
					disabled={signing || $stateConnectedWalletSigned}
				/>
			</div>
		</div>
	</Modal>
{/if}

<style>
	.address-group {

		display: flex;

		flex-direction: column;

		gap: 0.5rem;
}
	.login-via-wallet-content {

		display: flex;

		flex-direction: column;

		gap: 1.5rem;
}
	.error-box {

		margin-bottom: 0.25rem;

		display: flex;

		align-items: center;

		gap: 0.5rem;

		border-radius: 0.5rem;

		background-color: var(--component-error-background);

		padding: 0.5rem;

		color: var(--component-error-text);
}
	code {

		margin-top: 0.5rem;

		border-radius: 8px;

		border-width: 1px;

		border-color: var(--color-neutral-900);

		background-color: var(--color-background-300);

		padding: 0.5rem;

		display: block;

		width: 100%;

		min-width: 0px;

		max-width: 100%;

		overflow: hidden;

		white-space: nowrap;

		font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
		font-size: clamp(0.55rem, 2.7vw, 0.9rem);
		line-height: 1.15;
		letter-spacing: -0.01em;
}
	.spinner {
		width: auto !important;
		max-height: 100px !important;
		max-width: 100px !important;
	}
	.actions {

		display: flex;

		align-items: center;

		justify-content: flex-end;

		gap: 0.75rem;
}

	.warning-box {

		margin-bottom: 0.25rem;

		display: flex;

		align-items: center;

		gap: 0.5rem;

		border-radius: 0.5rem;

		background-color: var(--component-warning-background);

		padding: 0.5rem;

		color: var(--component-warning-text);
}
	.error-box :global(svg),
	.warning-box :global(svg) {
		color: inherit;
		flex-shrink: 0;
	}
</style>
